import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import { CsvDownloadResponse, isCsvDownloadResponse, readCsvResponse } from "../../utils/functions/readCsvResponse";
import { hasOwnKey, toNullableStr, toNumberOr, toStr, unwrapApiData } from "./points-service-helpers";
import {
  POINT_REDEMPTION_CANCELLATION_LABELS,
  PointRedemption,
  PointRedemptionCancellationReason,
  PointRedemptionFilters,
  PointRedemptionPage,
  PointRedemptionQuery,
  PointRedemptionStatus,
} from "../../models/pointRedemption";

export type PointRedemptionExportResponse = CsvDownloadResponse & { truncated: boolean };

const STORED_STATUSES: PointRedemptionStatus[] = ["pending", "fulfilled", "cancelled"];

const asRecord = (value: unknown): Record<string, unknown> =>
  (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;

const isStoredStatus = (value: unknown): value is PointRedemptionStatus =>
  typeof value === "string" && (STORED_STATUSES as string[]).includes(value);

const isCancellationReason = (value: unknown): value is PointRedemptionCancellationReason =>
  hasOwnKey(POINT_REDEMPTION_CANCELLATION_LABELS, value);

export const mapPointRedemption = (raw: unknown): PointRedemption => {
  const row = asRecord(raw);
  const user = asRecord(row.user);
  const reward = asRecord(row.reward);

  return {
    id: toNumberOr(row.id, 0),
    tenantId: toStr(row.tenant_id),
    user: {
      id: toNumberOr(user.id, 0),
      fullName: toStr(user.full_name),
      email: toStr(user.email),
    },
    reward: {
      id: toNumberOr(reward.id, 0),
      title: toStr(reward.title),
      partnerName: toNullableStr(reward.partner_name),
    },
    pointsSpent: toNumberOr(row.points_spent, 0),
    // Fuera de contrato no hay un estado correcto que mostrar: pendiente es el menos dañino.
    status: isStoredStatus(row.status) ? row.status : "pending",
    cancellationReason: isCancellationReason(row.cancellation_reason) ? row.cancellation_reason : null,
    qrShortCode: toStr(row.qr_short_code),
    createdAt: toStr(row.created_at),
    fulfilledAt: toNullableStr(row.fulfilled_at),
    cancelledAt: toNullableStr(row.cancelled_at),
    qrExpiresAt: toStr(row.qr_expires_at),
    isExpired: row.is_expired === true,
  };
};

export const mapPointRedemptionPage = (response: unknown): PointRedemptionPage => {
  const data = asRecord(unwrapApiData(response));
  const pagination = asRecord(data.pagination);
  const items = Array.isArray(data.items) ? data.items.map((row: unknown) => mapPointRedemption(row)) : [];
  return {
    items,
    page: toNumberOr(pagination.page, 1),
    limit: toNumberOr(pagination.limit, items.length),
    total: toNumberOr(pagination.total, items.length),
    totalPages: toNumberOr(pagination.totalPages, 1),
  };
};

/** Filtros → query params. Sin tenant_id el backend devuelve todas las instituciones. */
export const toPointRedemptionParams = (filters: PointRedemptionFilters): Record<string, string | number> => ({
  ...(filters.tenantId !== null ? { tenant_id: filters.tenantId } : {}),
  ...(filters.status !== null ? { status: filters.status } : {}),
  ...(filters.from !== null ? { from: filters.from } : {}),
  ...(filters.to !== null ? { to: filters.to } : {}),
  ...(filters.rewardId !== null ? { reward_id: filters.rewardId } : {}),
});

export const pointRedemptionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPointRedemptions: build.query<PointRedemptionPage, PointRedemptionQuery>({
      query: ({ page, limit, ...filters }) => ({
        url: API_ENDPOINTS.POINTS.REDEMPTIONS,
        params: { ...toPointRedemptionParams(filters), page, limit },
      }),
      transformResponse: (response: unknown) => mapPointRedemptionPage(response),
      providesTags: (_result, _error, { tenantId }) => [
        { type: "pointRedemptions" as const, id: tenantId ?? "ALL" },
      ],
    }),

    // Mutación y no query: el CSV no se cachea (trae nombre y email de gente).
    exportPointRedemptions: build.mutation<PointRedemptionExportResponse, PointRedemptionFilters>({
      query: (filters) => ({
        url: API_ENDPOINTS.POINTS.REDEMPTIONS_EXPORT,
        params: toPointRedemptionParams(filters),
        // El backend corta en 20.000 filas y lo avisa con X-Export-Truncated.
        responseHandler: async (response: Response) => {
          const result = await readCsvResponse(response);
          return isCsvDownloadResponse(response)
            ? { ...(result as object), truncated: response.headers.get("X-Export-Truncated") === "true" }
            : result;
        },
        validateStatus: isCsvDownloadResponse,
      }),
    }),
  }),
});

export const { useGetPointRedemptionsQuery, useExportPointRedemptionsMutation } = pointRedemptionsApi;
